import { useEffect, useRef, useState } from 'react';
import { CoreconfMark } from '../brand/CoreconfMark.js';
import { CURRENT } from './releaseChannel.js';
import { useUpdateStore } from './useUpdate.js';

/**
 * The launch check, the way a desktop client does it.
 *
 * The product already had an update engine with a rule attached: *an update
 * never interrupts you*. That rule was written about updates that arrive
 * mid-session, and it is why a release offers itself as a strip rather than
 * taking the screen — the overlay that used to do it covered the member list
 * and then the badge that says whether the operator can read your messages.
 *
 * Launch is the one moment the rule does not bind, because there is nothing to
 * interrupt: nobody is mid-sentence in an app that has not opened yet. So this
 * is the one place a blocking screen is honest, and it is the only place one
 * appears.
 *
 * ## What it actually does
 *
 * One manifest fetch, then one of two outcomes:
 *
 *   - the build on the server matches the one running → open immediately;
 *   - it does not → reload once, which is what "apply" means for a web client.
 *
 * ## The failure this could have introduced
 *
 * A reload loop. If the reload does not change the running build — a stale CDN
 * copy, a half-swapped webroot, a service worker holding the old bundle — the
 * naive version fetches the same manifest, sees the same mismatch and reloads
 * again, forever, with the user watching a splash screen that never ends.
 *
 * So the build it reloaded *for* is written to sessionStorage first. Coming
 * back and finding that the running build still is not that one means the
 * reload did not take, and the gate gives up and opens the app on the old
 * version rather than trying again. A slightly stale client is a working
 * client; a reload loop is a brick.
 *
 * ## And the one it could still introduce
 *
 * A check that hangs holds the door shut. `TIMEOUT_MS` is the whole budget:
 * when it expires the app opens regardless. Being one release behind is a
 * smaller problem than not starting, every time.
 */

/** Long enough for a slow connection, short enough not to read as a hang. */
const TIMEOUT_MS = 2500;

/** The build we reloaded in order to get. Survives the reload; nothing else does. */
const PENDING_KEY = 'coreconf:launch-update';

type Phase = 'checking' | 'updating' | 'done';

export function LaunchGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const check = useUpdateStore((s) => s.check);
  const [phase, setPhase] = useState<Phase>('checking');
  // StrictMode mounts twice in development. Without this the gate runs its
  // check, unmounts, and the second mount starts a second one — harmless here,
  // but the same guard shape that once left MLS stuck on "preparing" forever.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    let settled = false;
    const open = () => {
      if (settled) return;
      settled = true;
      setPhase('done');
    };

    // The door opens on this timer no matter what the network is doing.
    const timer = window.setTimeout(open, TIMEOUT_MS);

    void (async () => {
      try {
        await check('startup');
        const { manifest } = useUpdateStore.getState();
        if (settled || manifest === null) return open();

        if (manifest.buildId === CURRENT.buildId) {
          sessionStorage.removeItem(PENDING_KEY);
          return open();
        }

        // We already reloaded for this exact build and are still not running
        // it. The reload did not take; open on what we have rather than spin.
        if (sessionStorage.getItem(PENDING_KEY) === manifest.buildId) {
          sessionStorage.removeItem(PENDING_KEY);
          return open();
        }

        sessionStorage.setItem(PENDING_KEY, manifest.buildId);
        setPhase('updating');
        window.clearTimeout(timer);
        // A beat, so the state is readable rather than a flash of text nobody
        // can parse before the page goes away.
        window.setTimeout(() => location.reload(), 450);
      } catch {
        open();
      }
    })();

    return () => window.clearTimeout(timer);
  }, [check]);

  if (phase === 'done') return <>{children}</>;

  return (
    <div className="launch-gate" role="status" aria-live="polite">
      <CoreconfMark size={56} />
      <span className="launch-gate-word">coreconf</span>
      <span className="launch-gate-status">
        {phase === 'updating' ? 'Güncelleme uygulanıyor…' : 'Güncellemeler denetleniyor…'}
      </span>
      <span className="ui-dots launch-gate-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}
